import { useState } from "react";
import { GENRES, numJP, priceJP, type Work } from "../data";
import type { Nav, ToastInfo } from "../types";
import { GenreBadge, LoadingBlock, Placeholder } from "../components/atoms";
import { useFetch } from "../hooks";
import { fetchMySales, fetchMyWorks } from "../api";
import { useAuth } from "../auth";
import { useT } from "../i18n";

type Section = "works" | "sales" | "stats" | "settings";

interface Props {
  nav: Nav;
  onToast?: (t: ToastInfo) => void;
}

export function DashboardPage({ nav, onToast }: Props) {
  const t = useT();
  const { user } = useAuth();
  const [section, setSection] = useState<Section>("works");
  const worksFetch = useFetch(fetchMyWorks, []);
  const salesFetch = useFetch(fetchMySales, []);

  return (
    <div className="page page-dash">
      <div className="dash-layout">
        <nav className="dash-nav">
          <div
            style={{
              padding: "0 20px 16px",
              borderBottom: "1px solid var(--border)",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: "var(--muted-2)",
                fontFamily: "var(--font-mono)",
                textTransform: "uppercase",
                letterSpacing: "0.15em",
              }}
            >
              {t("dash.creator")}
            </div>
            <div style={{ color: "var(--accent)", fontWeight: 700, marginTop: 4 }}>
              {user?.displayName ?? "—"}
            </div>
            <div
              style={{
                color: "var(--muted-2)",
                fontSize: 10,
                fontFamily: "var(--font-mono)",
              }}
            >
              {user?.email ?? ""} · {t("dash.plan")}
            </div>
          </div>

          <div className="dnav-section">{t("dash.create")}</div>
          <DashNavItem
            active={section === "works"}
            icon="▣"
            label={t("dash.works")}
            meta={String(worksFetch.data?.length ?? 0)}
            onClick={() => setSection("works")}
          />
          <DashNavItem
            active={section === "sales"}
            icon="¥"
            label={t("dash.sales")}
            onClick={() => setSection("sales")}
          />
          <DashNavItem
            active={section === "stats"}
            icon="⌐"
            label={t("dash.stats")}
            onClick={() => setSection("stats")}
          />

          <div className="dnav-section">{t("dash.account")}</div>
          <DashNavItem
            active={section === "settings"}
            icon="⚙"
            label={t("dash.settings")}
            onClick={() => setSection("settings")}
          />
          <DashNavItem
            icon="↗"
            label={t("dash.openInStudio")}
            onClick={() => onToast?.({ kind: "info", text: t("dash.studioMove") })}
          />
          <DashNavItem
            icon="@"
            label={t("dash.publicProfile")}
            onClick={() => user && nav({ page: "author", id: user.id })}
          />

          <div style={{ flex: 1 }}></div>
          <div style={{ padding: "20px 20px 0", borderTop: "1px solid var(--border)" }}>
            <div
              style={{
                fontSize: 10,
                color: "var(--muted-2)",
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              {t("dash.apiStatus")}
            </div>
            <div
              style={{
                marginTop: 4,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--success)",
              }}
            >
              ● api.pixadv.net
            </div>
            <div
              style={{
                marginTop: 2,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--success)",
              }}
            >
              ● play.pixadv.net
            </div>
            <div
              style={{
                marginTop: 2,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--success)",
              }}
            >
              ● studio.pixadv.net
            </div>
          </div>
        </nav>

        <main className="dash-main">
          {section === "works" && (
            <WorksSection works={worksFetch.data ?? []} nav={nav} onToast={onToast} />
          )}
          {section === "sales" && <SalesSection sales={salesFetch.data ?? []} />}
          {section === "stats" && <StatsSection works={worksFetch.data ?? []} />}
          {section === "settings" && <SettingsSection onToast={onToast} />}
        </main>
      </div>
    </div>
  );
}

function DashNavItem({
  active,
  icon,
  label,
  meta,
  onClick,
}: {
  active?: boolean;
  icon: string;
  label: string;
  meta?: string;
  onClick: () => void;
}) {
  return (
    <a
      href="#"
      className={`dnav-item ${active ? "active" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <span className="ic">{icon}</span> {label}
      {meta && <span className="meta">{meta}</span>}
    </a>
  );
}

function WorksSection({
  works,
  nav,
  onToast,
}: {
  works: Work[];
  nav: Nav;
  onToast?: (t: ToastInfo) => void;
}) {
  const t = useT();
  const myPublic = works.filter((w) => !w.isDraft && !w.isPrivate);
  const myDrafts = works.filter((w) => w.isDraft);
  const myPrivate = works.filter((w) => w.isPrivate);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 6,
        }}
      >
        <div>
          <h1>{t("dash.works")}</h1>
          <div className="dash-sub">
            {t("dash.worksSub", {
              pub: myPublic.length,
              draft: myDrafts.length,
              priv: myPrivate.length,
            })}
          </div>
        </div>
        <button
          className="btn btn-accent"
          onClick={() => onToast?.({ kind: "info", text: t("dash.studioNewWork") })}
        >
          {t("dash.newWork")}
        </button>
      </div>

      <div className="dash-statbar">
        <StatCard label={t("dash.monthlyPlays")} value={numJP(8420)} delta="▲ 12.4%" />
        <StatCard
          label={t("dash.monthlySales")}
          value={`¥${numJP(64200)}`}
          delta="▲ 9.8%"
        />
        <StatCard label={t("dash.newFollowers")} value="+182" delta="▲ 8.0%" />
        <StatCard
          label={t("dash.avgRating")}
          value="4.75"
          delta={t("dash.sameAsLast")}
          deltaColor="var(--muted-2)"
        />
      </div>

      <div className="library-filter">
        <button className="lftab active">
          {t("dash.filterAll")} <span className="count">{works.length}</span>
        </button>
        <button className="lftab">
          {t("dash.filterPublished")} <span className="count">{myPublic.length}</span>
        </button>
        <button className="lftab">
          {t("dash.filterDraft")} <span className="count">{myDrafts.length}</span>
        </button>
        <button className="lftab">
          {t("dash.filterPrivate")} <span className="count">{myPrivate.length}</span>
        </button>
        <div style={{ flex: 1 }}></div>
        <button className="lftab" style={{ color: "var(--muted-2)" }}>
          {t("dash.sortUpdated")}
        </button>
      </div>

      <div className="dash-work-list">
        {works.map((w, i) => {
          const stateLabel = w.isDraft
            ? t("dash.stateDraft")
            : w.isPrivate
              ? t("dash.statePrivate")
              : t("dash.statePublished");
          const stateCls = w.isDraft ? "draft" : w.isPrivate ? "private" : "published";
          return (
            <div className="dash-work-item" key={w.id}>
              <Placeholder
                caption=""
                tone={GENRES[w.genre]?.tone}
                seed={i + 1}
                className="dw-thumb"
              />
              <div>
                <div className="dw-title">{w.title}</div>
                <div className="dw-id">
                  {w.id} · {w.updatedAt}
                  {w.price ? ` · ${priceJP(w.price)}` : ""}
                </div>
                <div
                  style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}
                >
                  <span className={`badge badge-state ${stateCls}`}>● {stateLabel}</span>
                  {!w.isDraft && <GenreBadge genre={w.genre} />}
                  {(w.tags || []).slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className="tag"
                      style={{ fontSize: 10, padding: "1px 6px" }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="dw-stat">
                <div className="dn">{w.isDraft ? "—" : numJP(w.stats.plays)}</div>
                <div className="dl">{t("dash.colPlays")}</div>
              </div>
              <div className="dw-stat">
                <div className="dn">{w.isDraft ? "—" : numJP(w.stats.favorites)}</div>
                <div className="dl">{t("dash.colFavs")}</div>
              </div>
              <div className="dw-stat">
                <div
                  className="dn"
                  style={{ color: w.isDraft ? "var(--muted-2)" : "var(--accent)" }}
                >
                  {w.isDraft
                    ? "—"
                    : w.price
                      ? `¥${numJP(w.stats.sales * (w.price ?? 0))}`
                      : "—"}
                </div>
                <div className="dl">{t("dash.colRevenue")}</div>
              </div>
              <div className="dash-actions-cell">
                <button
                  className="btn btn-sm"
                  onClick={() => onToast?.({ kind: "info", text: t("dash.studioEdit") })}
                >
                  {t("dash.btnEdit")}
                </button>
                {!w.isDraft && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => nav({ page: "work", id: w.id })}
                  >
                    {t("dash.btnView")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {works.length === 0 && <LoadingBlock height={120} />}
      </div>
    </>
  );
}

function StatCard({
  label,
  value,
  delta,
  deltaColor,
  valueColor,
  valueSize,
}: {
  label: string;
  value: string;
  delta: string;
  deltaColor?: string;
  valueColor?: string;
  valueSize?: number;
}) {
  return (
    <div className="stat-card">
      <div className="slabel">{label}</div>
      <div className="sval" style={{ color: valueColor, fontSize: valueSize }}>
        {value}
      </div>
      <div className="sdelta" style={{ color: deltaColor }}>
        {delta}
      </div>
    </div>
  );
}

function SalesSection({ sales }: { sales: Array<{ m: string; revenue: number }> }) {
  const t = useT();
  const total = sales.reduce((a, b) => a + b.revenue, 0);
  const max = Math.max(...sales.map((m) => m.revenue), 1);

  return (
    <>
      <h1>{t("dash.sales")}</h1>
      <div className="dash-sub">{t("dash.salesSub")}</div>

      <div className="dash-statbar">
        <StatCard
          label={t("dash.statMonthlyEstimate")}
          value={`¥${numJP(sales[sales.length - 1]?.revenue ?? 0)}`}
          delta="▲ 16.2%"
        />
        <StatCard
          label={t("dash.statTotal")}
          value={`¥${numJP(total)}`}
          delta={t("dash.statPast7")}
        />
        <StatCard
          label={t("dash.statPlatformFee")}
          value="15%"
          valueColor="var(--text)"
          delta={t("dash.statStripeIncl")}
          deltaColor="var(--muted-2)"
        />
        <StatCard
          label={t("dash.statNextPayout")}
          value={`¥${numJP(112340)}`}
          valueColor="var(--text)"
          valueSize={18}
          delta="2026-05-25"
          deltaColor="var(--primary)"
        />
      </div>

      <div className="chart">
        <div className="chart-head">
          <h3>{t("dash.chartMonthly")}</h3>
          <span className="chart-tot">¥{numJP(total)}</span>
        </div>
        <svg viewBox="0 0 700 220" preserveAspectRatio="none" style={{ height: 220 }}>
          {[0, 50, 100, 150, 200].map((y) => (
            <line key={y} x1="40" y1={y + 10} x2="690" y2={y + 10} stroke="#1f2530" strokeWidth="1" />
          ))}
          {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
            <text
              key={i}
              x="32"
              y={210 - p * 200 + 4}
              fill="#557799"
              fontSize="9"
              fontFamily="monospace"
              textAnchor="end"
            >
              {Math.round((p * max) / 1000)}k
            </text>
          ))}
          {sales.map((m, i) => {
            const w = 60;
            const x = 60 + i * (700 / Math.max(sales.length, 1) - 28);
            const h = (m.revenue / max) * 180;
            const y = 200 - h + 10;
            const isLast = i === sales.length - 1;
            return (
              <g key={m.m}>
                <rect
                  x={x}
                  y={y}
                  width={w}
                  height={h}
                  fill={isLast ? "#ffd28a" : "#66aaff"}
                  opacity={isLast ? 1 : 0.85}
                  rx="2"
                />
                <text
                  x={x + w / 2}
                  y={205}
                  fill="#88aacc"
                  fontSize="10"
                  fontFamily="monospace"
                  textAnchor="middle"
                >
                  {m.m.slice(5)}
                </text>
                <text
                  x={x + w / 2}
                  y={y - 6}
                  fill={isLast ? "#ffd28a" : "#aab2c0"}
                  fontSize="10"
                  fontFamily="monospace"
                  textAnchor="middle"
                  fontWeight="600"
                >
                  ¥{Math.round(m.revenue / 1000)}k
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16, marginTop: 16 }}>
        <div className="sidebar-panel">
          <h3>{t("dash.payouts")}</h3>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr
                style={{
                  color: "var(--muted-2)",
                  fontFamily: "var(--font-mono)",
                  textAlign: "left",
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                <th style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  {t("dash.payoutDate")}
                </th>
                <th style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  {t("dash.payoutAmount")}
                </th>
                <th style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  {t("dash.payoutStatus")}
                </th>
                <th style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  {t("dash.payoutRef")}
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                { d: "2026-04-25", a: 121400, id: "pi_4f9a01..." },
                { d: "2026-03-25", a: 134200, id: "pi_2c1b03..." },
                { d: "2026-02-25", a: 108800, id: "pi_8e7720..." },
                { d: "2026-01-25", a: 112300, id: "pi_0d8810..." },
              ].map((p, i) => (
                <tr key={i}>
                  <td
                    style={{
                      padding: "8px 0",
                      borderBottom: "1px solid var(--border)",
                      fontFamily: "var(--font-mono)",
                      color: "var(--sub)",
                    }}
                  >
                    {p.d}
                  </td>
                  <td
                    style={{
                      padding: "8px 0",
                      borderBottom: "1px solid var(--border)",
                      fontFamily: "var(--font-mono)",
                      color: "var(--accent)",
                    }}
                  >
                    ¥{numJP(p.a)}
                  </td>
                  <td style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                    <span
                      style={{
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 2,
                        color: "var(--success)",
                        border: "1px solid rgba(136,255,136,0.3)",
                        background: "rgba(136,255,136,0.05)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      ● {t("dash.payoutDone")}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "8px 0",
                      borderBottom: "1px solid var(--border)",
                      fontFamily: "var(--font-mono)",
                      color: "var(--muted-2)",
                      fontSize: 11,
                    }}
                  >
                    {p.id}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="sidebar-panel">
          <h3>Stripe Connect</h3>
          <div style={{ padding: "10px 0", display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 6,
                background: "#635bff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--font-mono)",
                fontWeight: 700,
                color: "#fff",
              }}
            >
              S
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: "var(--text)", fontWeight: 600, fontSize: 13 }}>
                {t("dash.stripeLinked")}
              </div>
              <div
                style={{
                  color: "var(--muted-2)",
                  fontSize: 11,
                  fontFamily: "var(--font-mono)",
                }}
              >
                acct_1NfP4...x9aF · JPY
              </div>
            </div>
            <span
              style={{
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 2,
                color: "var(--success)",
                border: "1px solid rgba(136,255,136,0.3)",
                background: "rgba(136,255,136,0.05)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {t("dash.stripeActive")}
            </span>
          </div>
          <div className="divider"></div>
          <div
            style={{
              fontSize: 11,
              color: "var(--muted-2)",
              lineHeight: 1.7,
              fontFamily: "var(--font-mono)",
              marginBottom: 10,
            }}
          >
            {t("dash.stripeFee", { pct: 15 })}
          </div>
          <button className="btn btn-sm btn-block">{t("dash.stripeOpen")}</button>
        </div>
      </div>
    </>
  );
}

function StatsSection({ works }: { works: Work[] }) {
  const t = useT();
  const publishedWorks = works.filter((w) => !w.isDraft);
  return (
    <>
      <h1>{t("dash.stats")}</h1>
      <div className="dash-sub">{t("dash.statsSub")}</div>

      <div className="dash-statbar">
        <StatCard
          label={t("dash.statTotalPlays")}
          value={numJP(publishedWorks.reduce((a, b) => a + b.stats.plays, 0))}
          delta={t("dash.statWeeklyDelta", { n: numJP(2418) })}
        />
        <StatCard
          label={t("dash.statTotalFavs")}
          value={numJP(publishedWorks.reduce((a, b) => a + b.stats.favorites, 0))}
          delta={t("dash.statWeeklyDelta", { n: 142 })}
        />
        <StatCard
          label={t("dash.statReviewCount")}
          value={numJP(publishedWorks.reduce((a, b) => a + (b.reviews ?? 0), 0))}
          delta={t("dash.statAvgRating", { n: "4.75" })}
        />
        <StatCard
          label={t("dash.statClearRate")}
          value="68%"
          delta={t("dash.statIndustryAvg")}
          deltaColor="var(--muted-2)"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
        <div className="chart">
          <div className="chart-head">
            <h3>{t("dash.workByStats")}</h3>
            <span
              className="chart-tot mono"
              style={{ color: "var(--muted-2)" }}
            >
              {t("dash.last30d")}
            </span>
          </div>
          {publishedWorks.map((w) => (
            <div key={w.id} style={{ marginBottom: 14 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 4,
                }}
              >
                <span style={{ color: "var(--accent)", fontSize: 13, fontWeight: 700 }}>
                  {w.title}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    color: "var(--muted-2)",
                  }}
                >
                  PV {numJP(w.stats.views)} · ▶ {numJP(w.stats.plays)} · ¥{" "}
                  {numJP(w.stats.sales)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  height: 18,
                  borderRadius: 3,
                  overflow: "hidden",
                  background: "var(--panel-2)",
                  border: "1px solid var(--border)",
                }}
              >
                <div
                  style={{
                    width: `35%`,
                    background: "rgba(102,170,255,0.5)",
                  }}
                ></div>
                <div
                  style={{
                    width: `${(w.stats.plays / Math.max(w.stats.views, 1)) * 35}%`,
                    background: "var(--primary)",
                  }}
                ></div>
                <div
                  style={{
                    width: `${(w.stats.sales / Math.max(w.stats.views, 1)) * 35}%`,
                    background: "var(--accent)",
                  }}
                ></div>
              </div>
              <div
                style={{
                  marginTop: 2,
                  display: "flex",
                  gap: 12,
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--muted-2)",
                }}
              >
                <span>■ PV</span>
                <span>■ {t("dash.colPlays").toLowerCase()}</span>
                <span>■ {t("dash.colRevenue").toLowerCase()}</span>
                <span style={{ marginLeft: "auto" }}>
                  {((w.stats.sales / Math.max(w.stats.views, 1)) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          ))}
        </div>

        <div>
          <div className="sidebar-panel">
            <h3>{t("dash.referrers")}</h3>
            {[
              { src: "pixiv.net", pct: 38 },
              { src: "twitter.com", pct: 24 },
              { src: "(direct)", pct: 18 },
              { src: "google.com", pct: 12 },
              { src: "discord.com", pct: 6 },
              { src: "(other)", pct: 2 },
            ].map((r) => (
              <Bar key={r.src} label={r.src} pct={r.pct} color="var(--accent)" />
            ))}
          </div>

          <div className="sidebar-panel">
            <h3>{t("dash.genreShare")}</h3>
            {[
              { g: "百合 × ミステリ", pct: 54 },
              { g: "百合 × 日常", pct: 22 },
              { g: "百合 × 全年齢", pct: 18 },
              { g: "その他", pct: 6 },
            ].map((g) => (
              <Bar key={g.g} label={g.g} pct={g.pct} color="var(--primary)" />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function Bar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          marginBottom: 2,
        }}
      >
        <span style={{ color: "var(--sub)" }}>{label}</span>
        <span style={{ color }}>{pct}%</span>
      </div>
      <div style={{ height: 4, background: "var(--panel-2)", borderRadius: 2 }}>
        <div
          style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 2 }}
        ></div>
      </div>
    </div>
  );
}

function SettingsSection({ onToast }: { onToast?: (t: ToastInfo) => void }) {
  const t = useT();
  return (
    <>
      <h1>{t("dash.settings")}</h1>
      <div className="dash-sub">{t("dash.settingsSub")}</div>

      <div className="sidebar-panel">
        <h3>{t("dash.payout")}</h3>
        <div className="field">
          <label>{t("dash.bankName")}</label>
          <input defaultValue="—— 銀行" />
        </div>
        <div className="field">
          <label>{t("dash.branchName")}</label>
          <input defaultValue="◯◯支店" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field">
            <label>{t("dash.acctType")}</label>
            <input defaultValue="普通" />
          </div>
          <div className="field">
            <label>{t("dash.acctNumber")}</label>
            <input defaultValue="1234567" />
          </div>
        </div>
        <div className="field">
          <label>{t("dash.acctHolder")}</label>
          <input defaultValue="アオヅキ ブンコ" />
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => onToast?.({ kind: "success", text: t("dash.savedPayout") })}
        >
          {t("dash.save")}
        </button>
      </div>

      <div className="sidebar-panel">
        <h3>{t("dash.tokushoho")}</h3>
        <div
          style={{
            fontSize: 11,
            color: "var(--muted-2)",
            lineHeight: 1.7,
            marginBottom: 14,
            fontFamily: "var(--font-mono)",
          }}
        >
          {t("dash.tokushohoIntro")}
        </div>
        <div className="field">
          <label>{t("dash.sellerName")}</label>
          <input defaultValue="蒼月 文" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field">
            <label>{t("dash.address")}</label>
            <input defaultValue="請求時に開示" />
          </div>
          <div className="field">
            <label>{t("dash.phone")}</label>
            <input defaultValue="請求時に開示" />
          </div>
        </div>
        <div className="field">
          <label>{t("dash.contactEmail")}</label>
          <input defaultValue="aozuki@example.com" />
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => onToast?.({ kind: "success", text: t("dash.savedGeneric") })}
          >
            {t("dash.save")}
          </button>
          <span
            style={{
              fontSize: 11,
              color: "var(--success)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {t("dash.publishedNote", { date: "26.05.10 14:22" })}
          </span>
        </div>
      </div>
    </>
  );
}
